import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ExportMenu, type ExportMenuProps } from './ExportMenu.js';

/* ===========================================================================
 * A LIST'S EXPORT IS A PAGE HEADER ACTION, NEVER A BAR CONTROL (owner,
 * 2026-11-02: "any export button should be as action button on top not on
 * action bar!"; design-rules §2).
 *
 * Merges E1–E8 put every list's `ExportMenu` in the ControlBar's `export`
 * slot, and that slot is gone. The file is built where the list's filters
 * are — often a toolbar several components below the page — so rather than
 * lift every filter into every page, the export PORTALS into the header's
 * action cluster that `PageShell` lends through `HeaderExportSlot`.
 *
 *   • It sits FIRST in the cluster: a secondary verb, before the page's own
 *     (PageActions: "a secondary outline, then a primary").
 *   • It is the QUIET outline at the header's 40px box — the cluster's
 *     secondary anatomy, so it never reads as the page's primary.
 *   • On the Overview the open panel draws it, so it changes with the panel.
 *
 * Outside a `PageShell` (full screen, a component tested alone) there is no
 * header, and nothing renders — never a fallback into the bar.
 * ======================================================================== */

export interface HeaderExportSlotValue {
  /** The span in the header's action cluster, once it is mounted. */
  host: HTMLElement | null;
  /** Ask for the host; the returned release gives it back. */
  claim: () => () => void;
}

export const HeaderExportSlot = createContext<HeaderExportSlotValue | null>(null);

/** `PageShell`'s half: the context value, and whether anything claimed it. */
export function useHeaderExportSlot(): {
  value: HeaderExportSlotValue;
  claimed: boolean;
  setHost: (el: HTMLElement | null) => void;
} {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => n - 1);
  }, []);
  const value = useMemo(() => ({ host, claim }), [host, claim]);
  return { value, claimed: claims > 0, setHost };
}

/**
 * The header's actions with the host span first in the cluster (`contents`:
 * no box of its own). ONE SHAPE whether or not an export has claimed it, so
 * the page's own actions never remount when a list's file arrives.
 */
export function headerExportHost(
  claimed: boolean,
  setHost: (el: HTMLElement | null) => void,
  actions: ReactNode
): ReactNode {
  if (!claimed && (actions == null || actions === false || actions === '')) return actions;
  return (
    <>
      {claimed && <span ref={setHost} data-page-header-export="" className="contents" />}
      {actions}
    </>
  );
}

/** A list's export, drawn in the page header's actions. */
export function HeaderExport(props: ExportMenuProps): JSX.Element | null {
  const slot = useContext(HeaderExportSlot);
  const claim = slot?.claim;
  useEffect(() => claim?.(), [claim]);
  if (!slot?.host) return null;
  return createPortal(<ExportMenu tone="quiet" {...props} size="header" />, slot.host);
}
